import { useCallback, useEffect, useId, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const isDocumentDark = () => {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.classList.contains('dark')) return true;
  return Boolean(document.querySelector('.sb-story-preview.dark'));
};

const normalizePosition = (value) => (value === 'left' ? 'left' : 'right');
const scrollLockMap = new WeakMap();
const openDrawerStack = [];

const lockScroll = (target) => {
  const state = scrollLockMap.get(target);
  if (state) {
    state.count += 1;
    return;
  }
  scrollLockMap.set(target, {
    count: 1,
    previousOverflow: target.style.overflow,
  });
  target.style.overflow = 'hidden';
};

const unlockScroll = (target) => {
  const state = scrollLockMap.get(target);
  if (!state) return;
  state.count -= 1;
  if (state.count > 0) return;
  target.style.overflow = state.previousOverflow;
  scrollLockMap.delete(target);
};

const pushDrawer = (id) => {
  if (!openDrawerStack.includes(id)) openDrawerStack.push(id);
};

const popDrawer = (id) => {
  const idx = openDrawerStack.lastIndexOf(id);
  if (idx >= 0) openDrawerStack.splice(idx, 1);
};

const isTopDrawer = (id) => openDrawerStack[openDrawerStack.length - 1] === id;

/**
 * Side drawer shell with free-form children content.
 * Consumers own inner layout (header/body/footer) entirely.
 *
 * @param {HTMLElement} [portalTarget] — Portal root (e.g. `#shell-main-content`). Defaults to `document.body`
 *   (`fixed` + `h-screen` + backdrop blur). When set, uses `absolute` + `h-full` and no backdrop blur so host
 *   chrome outside the portal root stays sharp.
 * @param {string} [heightClassName] — Panel height classes. If omitted: `h-screen` for body portal, `h-full` for a nested portal.
 * @param {string} [backdropZClassName='z-[100]'] — Backdrop z-index utility (raise when stacking drawers).
 * @param {string} [panelZClassName='z-[101]'] — Panel z-index utility (must sit above its backdrop).
 */
const Drawer = ({
  isOpen,
  onClose,
  children,
  portalTarget,
  position = 'right',
  widthClassName = 'w-[480px]',
  heightClassName,
  offsetClassName = 'top-0',
  lightBgClassName = 'bg-white',
  darkBgClassName = 'dark:bg-gray-900',
  backdropClassName = '',
  panelClassName = '',
  backdropZClassName = 'z-[100]',
  panelZClassName = 'z-[101]',
  closeOnBackdrop = true,
  closeOnEscape = true,
  ariaLabel = 'Drawer panel',
}) => {
  const hasDocument = typeof document !== 'undefined';

  const instanceId = useId();

  const side = normalizePosition(position);
  const isValidPortalTarget =
    hasDocument && portalTarget instanceof HTMLElement;
  const resolvedPortalTarget =
    isValidPortalTarget ? portalTarget : hasDocument ? document.body : null;
  const isBodyPortal = hasDocument && resolvedPortalTarget === document.body;
  const layerPositionClass = isBodyPortal ? 'fixed' : 'absolute';
  const resolvedHeight =
    heightClassName !== undefined && heightClassName !== ''
      ? heightClassName
      : isBodyPortal
        ? 'h-screen'
        : 'h-full';
  const backdropBlurClass = isBodyPortal ? 'backdrop-blur-sm' : '';

  /**
   * Portal wrapper: mirror `class="dark"` from `<html>` (or Storybook preview) so tokens
   * (`bg-surface`, `text-content`) apply while the drawer is open.
   */
  const [portalRootDark, setPortalRootDark] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen || !hasDocument) {
      setPortalRootDark(false);
      return undefined;
    }

    const sync = () => setPortalRootDark(isDocumentDark());
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const preview = document.querySelector('.sb-story-preview');
    if (preview) {
      observer.observe(preview, { attributes: true, attributeFilter: ['class'] });
    }

    return () => observer.disconnect();
  }, [isOpen, hasDocument]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onClose?.();
    },
    [closeOnBackdrop, onClose],
  );

  useEffect(() => {
    if (!isOpen || !hasDocument || !resolvedPortalTarget) return undefined;
    lockScroll(resolvedPortalTarget);
    return () => {
      unlockScroll(resolvedPortalTarget);
    };
  }, [isOpen, hasDocument, resolvedPortalTarget]);

  useEffect(() => {
    if (!isOpen) return undefined;
    pushDrawer(instanceId);
    return () => popDrawer(instanceId);
  }, [isOpen, instanceId]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape || !hasDocument) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (!isTopDrawer(instanceId)) return;
      onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, hasDocument, onClose, instanceId]);

  if (!hasDocument || !isOpen || !resolvedPortalTarget) return null;

  return createPortal(
    <div className={portalRootDark ? 'dark' : undefined}>
      <div
        className={[
          layerPositionClass,
          // Above shell mobile sidebar / overlays (z-40–50); below dropdown/toast layers.
          'inset-x-0 bottom-0 bg-black/60',
          backdropZClassName,
          backdropBlurClass,
          offsetClassName,
          backdropClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={handleBackdropClick}
        role="presentation"
      />
      <div
        className={[
          layerPositionClass,
          panelZClassName,
          'pointer-events-auto flex flex-col overflow-hidden shadow-2xl border-gray-200 dark:border-gray-700',
          side === 'left' ? 'left-0 border-r' : 'right-0 border-l',
          widthClassName,
          resolvedHeight,
          offsetClassName,
          lightBgClassName,
          darkBgClassName,
          panelClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>,
    resolvedPortalTarget,
  );
};

export default Drawer;
